import {Chip, Fab, Grid, Tooltip} from '@material-ui/core';
import {green, grey, red} from '@material-ui/core/colors';
import {
  Add,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  Close,
  CloudDownload,
  Delete,
  Edit,
  FilterList,
  FirstPage,
  Info,
  LastPage,
  PersonAdd,
  Search,
  VpnKey
} from '@material-ui/icons';
import MaterialTable, {MTableToolbar} from 'material-table';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {checkRolePermissions} from '../permissions/permissions';
import {
  deleteCredential,
  deleteEmployee,
  getCredentials,
  getEmployees,
  getPersons,
  saveEmployee
} from '../script/person-service.js';
import {addNotifications} from '../store/appActions';
import ConfirmationDialog from './ConfirmationDialog';
import EmployeeEditDialog from './EmployeeEditDialog';
import KeyManagamentDialog from './KeyManagementDialog.jsx';

const tableIcons = {
  Search: Search,
  Filter: FilterList,
  PreviousPage: ChevronLeft,
  NextPage: ChevronRight,
  FirstPage: FirstPage,
  LastPage: LastPage,
  Add: Add,
  Edit: Edit,
  Check: Check,
  Close: Close,
  SortArrow: ArrowDownward,
  ResetSearch: Clear
};

const tableLocalization = {
  toolbar: {
    searchTooltip: locale.tableSearchTooltip,
    searchPlaceholder: locale.tableSearchPlaceholder
  },
  header: {
    actions: locale.tableHeaderActions
  },
  body: {
    emptyDataSourceMessage: locale.tableEmptyDataSourceMessage
  },
  pagination: {
    labelRowsPerPage: locale.tableLabelRowsPerPage,
    labelDisplayedRows: locale.tableLabelDisplayedRows,
    labelRowsSelect: locale.tableLabelRowsSelect,
    firstTooltip: locale.tableFirstTooltip,
    previousTooltip: locale.tablePreviousTooltip,
    nextTooltip: locale.tableNextTooltip,
    lastTooltip: locale.tableLastTooltip
  }
};

const styles = {
  root: {marginTop: '3%'},
  toolbar: {padding: '10px 14px'}
};

const employee = {
  firstNames: '',
  officialName: '',
  birthDate: null,
  archiveAccess: [],
  new: true
};

const employeeColumns = [
  {title: 'id', field: 'id', hidden: true},
  {title: locale.name, field: 'lastname'},
  {title: locale.firstName, field: 'firstName'},
  {title: locale.organisation, field: 'organisation', hidden: true}
];

const tableOptions = {
  sorting: true,
  actionsColumnIndex: -1,
  showTitle: true,
  search: true
};

/**
 * Map the state to the properties.
 *
 * @param {Object} state - The state from store.
 * @return {Object} - The state.
 */
const mapStateToProps = (state: Object) => {
  return {
    user: state.user,
    roles: state.roles,
    archives: state.archives
  };
};

/**
 * Map the properties.
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    addNotifications: (notification) => dispatch(addNotifications(notification))
  };
}

/**
 * Connected component class of employee list.
 */
class ConnectedEmployeeList extends Component {
  // eslint-disable-next-line require-jsdoc
  constructor(props) {
    const administration = checkRolePermissions(
      props.roles,
      'VIEW',
      'Person Management'
    );
    if (!administration) {
      const notaryArchives = props.archives.map((access) => {
        const mappedAccess = {...access};
        mappedAccess.access = 'READ';
        return mappedAccess;
      });
      employee.archiveAccess = notaryArchives;
    }

    super(props);
    this.state = {
      tableData: [],
      employees: [],
      selectedEmployee: employee,
      showEditDialog: false,
      showDeleteDialog: false,
      showKeyDialog: false,
      keys: [],
      employeeToDelete: employee,
      administration: administration
    };
  }

  /**
   * Fetch list of employees.
   */
  fetchEmployees = () => {
    if (this.state.administration) {
      getPersons().then(this.handlePersonFetchResult);
      this.setState({administration: true});
    } else {
      getEmployees().then(this.handlePersonFetchResult);
    }
  };

  /**
   * Handle the result of fetchEmployees
   *
   * @param {Array} personList
   */
  handlePersonFetchResult = (personList: Array) => {
    const employees = [];
    personList.forEach(function (item) {
      const organization = item.organisation || {};

      employees.push({
        id: item.id,
        lastname: item.officialName,
        firstName: item.firstNames,
        userName: item.userName,
        type: item.type,
        keyCount: item.keyCount,
        organisation: organization.name
      });
    });
    this.setState({tableData: employees, employees: personList});
  };

  componentDidMount = () => {
    this.fetchEmployees();
  };

  /**
   * Function to display the document upload.
   */
  displayEmployeeAdd = () => {
    this.setState({
      showEditDialog: true
    });
  };

  /**
   * Renders add employee button.
   *
   * @return {string} - The button to be rendered.
   */
  renderEmployeeAddButton = () => {
    return (
      <Tooltip title={locale.addEmployeeButton} placement='top'>
        <Fab
          size='small'
          color='default'
          onClick={() => this.displayEmployeeAdd()}
        >
          <PersonAdd />
        </Fab>
      </Tooltip>
    );
  };

  /**
   * Save the employee from the employee dialog.
   *
   * @param {Object} employee the employee data.
   */
  saveEmploye = () => {
    this.setState({showEditDialog: false});
    saveEmployee(this.state.selectedEmployee).then((response) => {
      if (response.created) {
        this.props.addNotifications([
          new Notification(
            this.state.selectedEmployee.id
              ? locale.updateEmployeeCompleteNotification
              : locale.createEmployeeCompleteNotification,
            'success',
            6000
          )
        ]);
        this.setState({selectedEmployee: employee});
        this.fetchEmployees();
      } else {
        this.props.addNotifications([
          new Notification(
            this.state.selectedEmployee.id
              ? locale.updateEmployeeErrorNotification
              : locale.createEmployeeErrorNotification,
            'error',
            6000
          )
        ]);
      }
    });
  };

  closeEditDialog = () => {
    this.setState({
      showEditDialog: false,
      selectedEmployee: employee
    });
  };

  closeDeleteDialog = () => {
    this.setState({
      showDeleteDialog: false,
      employeeToDelete: employee
    });
  };

  closeKeyDialog = () => {
    this.setState({
      showKeyDialog: false,
      selectedEmployee: employee
    });
    this.fetchEmployees();
  };

  /**
   * Handle edit dialog access changes for selected employee.
   *
   * @param {Object} archiveAccess the archive name.
   * @param {string} access the new access level.
   */
  handleAccessChange = (archiveAccess: Object, access: string) => {
    archiveAccess.access = access;
    const archives = employee.archiveAccess;
    const employeeArchives = archives.map((a) => {
      if (a.archiveId === archiveAccess.archiveId) {
        a.access = access;
      }
      return a;
    });
    this.setState({
      selectedEmployee: {
        ...this.state.selectedEmployee,
        archiveAccess: employeeArchives
      }
    });
  };

  /**
   * Handle edit dialog data changes for selected employee.
   *
   * @param {string} field the field.
   * @param {*} value the new value.
   */
  handleValueChange = (field: string, value: *) => {
    const employee = {...this.state.selectedEmployee};
    employee[field] = value;
    this.setState({selectedEmployee: employee});
  };

  /**
   * Handle open edit dialog action.
   *
   * @param {Object} event the event.
   * @param {*} rowData the data from row.
   */
  openEditDialog = (event: Object, rowData: Object) => {
    this.setState({
      showEditDialog: true,
      selectedEmployee: this.employeeFromId(rowData.id)
    });
  };

  /**
   * Handle open edit dialog action.
   *
   * @param {Object} event the event.
   * @param {*} rowData the data from row.
   */
  openKeyDialog = (event: Object, rowData: Object) => {
    this.fetchCredentials(rowData.id);
    this.setState({
      showKeyDialog: true,
      selectedEmployee: this.employeeFromId(rowData.id)
    });
  };

  /**
   * Handle signature template download action.
   *
   * @param {Object} event the event.
   * @param {*} rowData the data from row.
   */
  downloadSignatureTemplate = (event: Object, rowData: Object) => {
    window.open('/persons/' + rowData.id + '/signature-declaration', '_blank');
  };

  /**
   * Fetch credentials for given person id.
   *
   * @param {string} personId
   */
  fetchCredentials = (personId: string) => {
    getCredentials(personId).then((response) => {
      this.setState({
        keys: response
      });
    });
  };

  /**
   * Handle open delete dialog action.
   *
   * @param {Object} event the event.
   * @param {*} rowData the data from row.
   */
  openDeleteDialog = (event: Object, rowData: Object) => {
    this.setState({
      showDeleteDialog: true,
      employeeToDelete: this.employeeFromId(rowData.id)
    });
  };

  /**
   * Get a employee from list by id.
   *
   * @param {*} id the id.
   * @return {Object} the matching employee.
   */
  employeeFromId = (id) => {
    return this.state.employees.find((e) => e.id === id);
  };

  /**
   * Delete the selected employee.
   */
  deleteEmployee = () => {
    if (this.state.employeeToDelete) {
      deleteEmployee(this.state.employeeToDelete.id).then((response) => {
        if (response.deleted) {
          this.props.addNotifications([
            new Notification(
              locale.deleteEmployeeCompleteNotification,
              'success',
              6000
            )
          ]);
          this.fetchEmployees();
        } else {
          this.props.addNotifications([
            new Notification(
              locale.deleteEmployeeErrorNotification,
              'error',
              6000
            )
          ]);
        }
      });
      this.setState({employeeToDelete: employee, showDeleteDialog: false});
    }
  };

  /**
   * Delete a secruity key.
   *
   *  @param {string} keyId
   */
  deleteCredential = (keyId: string) => {
    deleteCredential(this.state.selectedEmployee.id, keyId).then((response) => {
      if (response.updated) {
        this.props.addNotifications([
          new Notification(
            locale.deleteKeyCompleteNotification,
            'success',
            6000
          )
        ]);
        this.fetchCredentials(this.state.selectedEmployee.id);
      } else {
        this.props.addNotifications([
          new Notification(locale.deleteKeyErrorNotification, 'error', 6000)
        ]);
      }
    });
  };

  /**
   * Renders EmployeesList component.
   *
   * @return {string} - The employee list component to be rendered.
   */
  // eslint-disable-next-line max-lines-per-function, require-jsdoc
  render() {
    return (
      <div style={styles.root}>
        <MaterialTable
          icons={tableIcons}
          title={
            this.state.administration
              ? locale.personManagementTitle
              : locale.employeeListTitle
          }
          localization={tableLocalization}
          columns={
            this.state.administration
              ? [
                  {title: 'id', field: 'id', hidden: true},
                  {
                    title: locale.name,
                    field: 'lastname',
                    customSort: (a, b) => a.localeCompare(b)
                  },
                  {
                    title: locale.firstName,
                    field: 'firstName',
                    customSort: (a, b) => a.localeCompare(b)
                  },
                  {title: locale.userName, field: 'userName'},
                  {title: locale.personType, field: 'type'},
                  {
                    title: locale.pesonKeyCount,
                    field: 'keyCount',
                    render: (rowData: Object) => (
                      <Chip
                        variant='outlined'
                        size='small'
                        style={
                          rowData.keyCount === 0
                            ? {
                                backgroundColor: red[300],
                                color: grey[50],
                                width: '2em'
                              }
                            : {
                                backgroundColor: green[300],
                                color: grey[50],
                                width: '2em'
                              }
                        }
                        label={rowData.keyCount}
                      />
                    )
                  },
                  {title: locale.organisation, field: 'organisation'}
                ]
              : employeeColumns
          }
          data={this.state.tableData}
          options={tableOptions}
          components={{
            Toolbar: (props) => (
              <div>
                <MTableToolbar {...props} />
                <Grid container item xs={12} spacing={3} style={styles.toolbar}>
                  <Grid item xs={11}></Grid>
                  <Grid item xs={1}>
                    {!this.state.administration &&
                      this.renderEmployeeAddButton()}
                  </Grid>
                </Grid>
              </div>
            )
          }}
          actions={[
            {
              icon: CloudDownload,
              tooltip: locale.getSignatureTemplate,
              onClick: this.downloadSignatureTemplate,
              hidden: !this.state.administration
            },
            {
              icon: VpnKey,
              tooltip: locale.manageKeys,
              onClick: this.openKeyDialog,
              hidden: !this.state.administration
            },
            {
              icon: this.state.administration ? Info : Edit,
              tooltip: locale.editEmployee,
              onClick: this.openEditDialog
            },
            {
              icon: Delete,
              tooltip: locale.deleteEmployee,
              onClick: this.openDeleteDialog,
              hidden: this.state.administration
            }
          ]}
        />

        <EmployeeEditDialog
          open={this.state.showEditDialog}
          readOnly={this.state.administration}
          employee={this.state.selectedEmployee}
          handleValueChange={this.handleValueChange}
          handleAccessChange={this.handleAccessChange}
          handleSubmit={this.saveEmploye}
          handleClose={this.closeEditDialog}
        />
        <KeyManagamentDialog
          open={this.state.showKeyDialog}
          employee={this.state.selectedEmployee}
          keys={this.state.keys}
          handleClose={this.closeKeyDialog}
          refreshKeys={() =>
            this.fetchCredentials(this.state.selectedEmployee.id)
          }
          handleDelete={(key) => this.deleteCredential(key)}
        />
        <ConfirmationDialog
          open={this.state.showDeleteDialog}
          dialogText={locale.formatString(
            locale.deleteEmployeeDialogText,
            this.state.employeeToDelete.officialName,
            this.state.employeeToDelete.firstNames
          )}
          title={locale.deleteEmployeeDialogTitle}
          handleClose={this.closeDeleteDialog}
          handleSubmit={this.deleteEmployee}
        />
      </div>
    );
  }
}

ConnectedEmployeeList.propTypes = {
  roles: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
  archives: PropTypes.array.isRequired,
  addNotifications: PropTypes.func.isRequired
};

const EmployeeList = connect(
  mapStateToProps,
  mapDispatchToProps
)(ConnectedEmployeeList);
export default EmployeeList;
